import { supabase } from './supabase';
import type { Message, TimelineItem, CommunicationCard } from '../types/messages';
import type { ConversationSummary } from './messageConversations';

// ─────────────────────────────────────────────────────────────────────────────
// Messages on the core tables (sms_messages, received_emails, call_history,
// agent_callbacks). One call builds the inbox, one call builds a thread, and
// read state lives on the core rows. Backed by the messages_* RPCs.
//
// messages.data_source = 'core' reads the core tables. Every business has that
// row, and a new business gets it on create. A missing row still falls back to
// the legacy all_messages path.
// ─────────────────────────────────────────────────────────────────────────────

const flagCache = new Map<string, { value: boolean; at: number }>();
const FLAG_TTL_MS = 60_000;

/** True when this business reads and writes messages through the core tables. */
export async function isCoreMessagesEnabled(businessId: string | null | undefined): Promise<boolean> {
  if (!businessId || !supabase) return false;
  const cached = flagCache.get(businessId);
  if (cached && Date.now() - cached.at < FLAG_TTL_MS) return cached.value;
  try {
    const { data, error } = await supabase.rpc('get_app_setting', {
      p_business_id: businessId,
      p_app_slug: 'messages',
      p_setting_key: 'data_source',
      p_default_value: '{"value":"legacy"}',
    });
    const raw: any = data;
    const value = !error && raw && typeof raw === 'object' && 'value' in raw ? String(raw.value) : 'legacy';
    const on = value === 'core';
    flagCache.set(businessId, { value: on, at: Date.now() });
    return on;
  } catch {
    return false;
  }
}

/** Synchronous read of the last answer, for render paths that cannot await. */
export function isCoreMessagesEnabledSync(businessId: string | null | undefined): boolean {
  if (!businessId) return false;
  return flagCache.get(businessId)?.value ?? false;
}

// ───────────────────────── sender identity ─────────────────────────

export type SenderKind = 'resource' | 'owner' | 'user' | 'automation' | 'bot' | 'phone_agent' | 'system';

export interface SenderIdentity {
  kind: SenderKind;
  id: string | null;
  name: string | null;
}

const senderCache = new Map<string, { value: SenderIdentity; at: number }>();
const SENDER_TTL_MS = 5 * 60_000;

// A process acting on the user's behalf (an automation run from this browser)
// can claim the sends it makes, so they are not stamped as the signed-in person.
let senderOverride: SenderIdentity | null = null;

/** Run `fn` with every send inside it stamped as `sender`. */
export async function withSender<T>(sender: SenderIdentity, fn: () => Promise<T>): Promise<T> {
  const previous = senderOverride;
  senderOverride = sender;
  try {
    return await fn();
  } finally {
    senderOverride = previous;
  }
}

/**
 * Who is sending from this browser session: the resource linked to the signed-in
 * user, else the business owner, else the plain user. Stamped on every outbound
 * text, email, and call the app itself places.
 */
export async function resolveCurrentSender(businessId: string): Promise<SenderIdentity> {
  if (senderOverride) return senderOverride;
  const fallback: SenderIdentity = { kind: 'user', id: null, name: null };
  if (!supabase || !businessId) return fallback;
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return fallback;
    const cacheKey = `${businessId}:${userId}`;
    const cached = senderCache.get(cacheKey);
    if (cached && Date.now() - cached.at < SENDER_TTL_MS) return cached.value;

    const [{ data: resource }, { data: business }] = await Promise.all([
      supabase
        .from('resources')
        .select('id, first_name, last_name')
        .eq('business_id', businessId)
        .eq('auth_user_id', userId)
        .maybeSingle(),
      supabase.from('businesses').select('owner_id, name').eq('id', businessId).maybeSingle(),
    ]);

    let value: SenderIdentity;
    if (resource?.id) {
      value = {
        kind: 'resource',
        id: resource.id,
        name: `${resource.first_name || ''} ${resource.last_name || ''}`.trim() || null,
      };
    } else if (business?.owner_id === userId) {
      const meta: any = auth?.user?.user_metadata || {};
      value = {
        kind: 'owner',
        id: userId,
        name: meta.full_name || meta.name || auth?.user?.email || business?.name || null,
      };
    } else {
      value = { kind: 'user', id: userId, name: auth?.user?.email || null };
    }
    senderCache.set(cacheKey, { value, at: Date.now() });
    return value;
  } catch {
    return fallback;
  }
}

/** Column values for a core-table insert. */
export function senderColumns(s: SenderIdentity | null | undefined) {
  if (!s) return {};
  return { sender_kind: s.kind, sender_id: s.id, sender_name: s.name };
}

// ───────────────────────── keys ─────────────────────────

/** Ten-digit phone key, matching public.phone_key in the database. */
export function phoneKey(input: string | null | undefined): string | null {
  const digits = (input || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  if (digits.length >= 10) return digits.slice(-10);
  return null;
}

/** Bare lower-case address, matching public.email_key in the database. */
export function emailKey(input: string | null | undefined): string | null {
  if (!input) return null;
  const angle = input.match(/<([^>]+)>/);
  const bare = (angle ? angle[1] : input.split(',')[0]).trim().toLowerCase();
  return bare || null;
}

/** Server conversation key for a card: customer:<id> | phone:<key> | email:<key>. */
export function conversationKeyForCard(card: Pick<CommunicationCard, 'type' | 'customer_id' | 'contact_info'>): string {
  if (card.type === 'customer' && card.customer_id) return `customer:${card.customer_id}`;
  if (card.type === 'email') return `email:${emailKey(card.contact_info) || ''}`;
  return `phone:${phoneKey(card.contact_info) || (card.contact_info || '').replace(/\D/g, '')}`;
}

/** Card id the list uses for a server conversation key. */
export function cardIdForConversationKey(key: string): string {
  const sep = key.indexOf(':');
  const kind = key.slice(0, sep);
  const val = key.slice(sep + 1);
  if (kind === 'customer') return `customer_${val}`;
  if (kind === 'email') return `email_${val}`;
  return `sms_${val}`;
}

// ───────────────────────── inbox ─────────────────────────

export interface CoreConversationSummary extends ConversationSummary {
  has_open_lead: boolean;
  is_muted: boolean;
}

export type InboxTab = 'recent_or_unread' | 'unread' | 'unread_suppliers' | 'read' | 'spam' | 'all';

/**
 * One call for the conversation list. The default tab returns the most recent
 * `limit` threads plus every thread that still has unread.
 */
export async function fetchCoreInbox(
  businessId: string,
  opts: { tab?: InboxTab; search?: string | null; organization?: string; limit?: number; offset?: number } = {},
): Promise<CoreConversationSummary[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('messages_inbox', {
    p_business_id: businessId,
    p_tab: opts.tab ?? 'recent_or_unread',
    p_search: opts.search ?? null,
    p_organization: opts.organization ?? 'chronological',
    p_limit: opts.limit ?? 200,
    p_offset: opts.offset ?? 0,
  });
  if (error) {
    console.error('[messagesCore] messages_inbox error:', error);
    throw error;
  }
  return ((data || []) as any[]).map((r) => ({ ...r, unread_count: Number(r.unread_count) || 0 })) as CoreConversationSummary[];
}

export async function fetchCoreUnreadCounts(businessId: string): Promise<{ unread: number; unreadSuppliers: number }> {
  if (!supabase) return { unread: 0, unreadSuppliers: 0 };
  const { data, error } = await supabase.rpc('messages_unread_counts', { p_business_id: businessId });
  if (error) {
    console.error('[messagesCore] messages_unread_counts error:', error);
    return { unread: 0, unreadSuppliers: 0 };
  }
  const row: any = Array.isArray(data) ? data[0] : data;
  return { unread: Number(row?.unread) || 0, unreadSuppliers: Number(row?.unread_suppliers) || 0 };
}

// ───────────────────────── thread ─────────────────────────

export interface CoreThreadPage {
  items: TimelineItem[];
  /** Every message row on the page, newest first, emails not collapsed. */
  messages: Message[];
  hasMore: boolean;
  total: number;
}

const normalizeSubjectForThread = (subject: string | undefined | null): string =>
  (subject || '')
    .replace(/^(re:|fwd:|fw:)\s*/gi, '')
    .trim()
    .toLowerCase();

/**
 * One call for a conversation page, newest first. Items carry the same ids the
 * screen already keys on (message_/email_thread_/call_/callback_/milestone_),
 * and emails are collapsed to one bubble per subject thread like today.
 */
export async function fetchCoreThread(
  businessId: string,
  conversationKey: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<CoreThreadPage> {
  if (!supabase) return { items: [], messages: [], hasMore: false, total: 0 };
  const { data, error } = await supabase.rpc('messages_thread', {
    p_business_id: businessId,
    p_conversation_key: conversationKey,
    p_limit: opts.limit ?? 50,
    p_offset: opts.offset ?? 0,
  });
  if (error) {
    console.error('[messagesCore] messages_thread error:', error);
    throw error;
  }
  const page: any = data || {};
  const raw: any[] = Array.isArray(page.items) ? page.items : [];

  const items: TimelineItem[] = [];
  const emails: Message[] = [];
  const allMessages: Message[] = [];

  for (const r of raw) {
    if (r.type === 'milestone' && r.milestone) {
      items.push({ id: `milestone_${r.milestone.id}`, type: 'milestone', created_at: r.created_at, milestone: r.milestone });
      continue;
    }
    const m = r.message as any;
    if (!m) continue;
    const message: Message = {
      ...m,
      body: m.body || '',
      media_urls: Array.isArray(m.media_urls) ? m.media_urls : (m.media_urls ? m.media_urls : null),
      ai_response: !!m.ai_response,
    };
    allMessages.push(message);
    if (message.message_type === 'email') {
      emails.push(message);
    } else if (message.message_type === 'call') {
      items.push({ id: `call_${message.id}`, type: 'message', created_at: message.created_at, message });
    } else if (message.message_type === 'callback') {
      items.push({ id: `callback_${message.id}`, type: 'message', created_at: message.created_at, message });
    } else {
      items.push({ id: `message_${message.id}`, type: 'message', created_at: message.created_at, message });
    }
  }

  // Email: one bubble per subject thread, showing the latest message.
  const fallbackSubject = emails.find((e) => normalizeSubjectForThread(e.subject))?.subject || '';
  const latestBySubject = new Map<string, Message>();
  for (const e of emails) {
    const key = normalizeSubjectForThread(e.subject) || normalizeSubjectForThread(fallbackSubject) || `__no_subject_${e.id}`;
    const prev = latestBySubject.get(key);
    if (!prev || new Date(e.created_at).getTime() > new Date(prev.created_at).getTime()) latestBySubject.set(key, e);
  }
  latestBySubject.forEach((latest) => {
    items.push({ id: `email_thread_${latest.id}`, type: 'message', created_at: latest.created_at, message: latest });
  });

  items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  allMessages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return { items, messages: allMessages, hasMore: !!page.has_more, total: Number(page.total) || 0 };
}

// ───────────────────────── read state ─────────────────────────

/** Mark specific messages read. Ids may be core ids or legacy all_messages ids. */
export async function coreMarkItemsRead(businessId: string, itemIds: string[]): Promise<number> {
  if (!supabase || itemIds.length === 0) return 0;
  const ids = itemIds.filter((id) => id && !id.startsWith('optimistic_') && !id.startsWith('summary_'));
  if (ids.length === 0) return 0;
  const { data, error } = await supabase.rpc('messages_mark_read', {
    p_business_id: businessId,
    p_scope: 'items',
    p_conversation_key: null,
    p_item_ids: ids,
    p_include_suppliers: true,
  });
  if (error) {
    console.error('[messagesCore] messages_mark_read(items) error:', error);
    throw error;
  }
  return Number(data) || 0;
}

export type CoreReadKind = 'sms' | 'email' | 'call' | 'callback';

/** Everything on the phone side of a conversation: texts, missed calls, pending callbacks. */
export const PHONE_READ_KINDS: CoreReadKind[] = ['sms', 'call', 'callback'];

/**
 * Mark every unread item in one conversation read. With no `kinds` that is
 * texts, emails, missed calls and pending callbacks; pass `kinds` to clear
 * only some of them (a text reply clears the phone side and leaves email threads).
 */
export async function coreMarkConversationRead(
  businessId: string,
  conversationKey: string,
  kinds?: CoreReadKind[],
): Promise<number> {
  if (!supabase) return 0;
  const { data, error } = await supabase.rpc('messages_mark_read', {
    p_business_id: businessId,
    p_scope: 'conversation',
    p_conversation_key: conversationKey,
    p_item_ids: null,
    p_include_suppliers: true,
    p_kinds: kinds && kinds.length ? kinds : null,
  });
  if (error) {
    console.error('[messagesCore] messages_mark_read(conversation) error:', error);
    throw error;
  }
  return Number(data) || 0;
}

/** Mark everything read for the business. Suppliers and muted senders stay as they are unless asked. */
export async function coreMarkAllRead(businessId: string, includeSuppliers = false): Promise<number> {
  if (!supabase) return 0;
  const { data, error } = await supabase.rpc('messages_mark_read', {
    p_business_id: businessId,
    p_scope: 'all',
    p_conversation_key: null,
    p_item_ids: null,
    p_include_suppliers: includeSuppliers,
  });
  if (error) {
    console.error('[messagesCore] messages_mark_read(all) error:', error);
    throw error;
  }
  return Number(data) || 0;
}

export async function coreMarkItemsUnread(businessId: string, itemIds: string[]): Promise<number> {
  if (!supabase || itemIds.length === 0) return 0;
  const { data, error } = await supabase.rpc('messages_mark_unread', {
    p_business_id: businessId,
    p_item_ids: itemIds,
  });
  if (error) {
    console.error('[messagesCore] messages_mark_unread error:', error);
    throw error;
  }
  return Number(data) || 0;
}
